"use client"

import React, { useEffect, useState } from "react"
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom"
import { AuthProvider, useAuth } from "./contexts/auth-context"
import ChatInterface from "./components/chat/ChatInterface"
import Login from "./components/auth/Login"
import Plans from "./components/subscription/Plans"
import LandingPage from "./components/home/LandingPage"
import Settings from "./components/settings/Settings"
import { SubscriptionProvider } from "./contexts/subscription-context"
import { initializeRemoteConfig } from "./lib/remote-config"
import SharedDebateView from './components/chat/SharedDebateView'
import AboutPage from './components/home/AboutPage'
import TermsOfService from './components/home/TermsOfService'
import PrivacyPolicy from './components/home/PrivacyPolicy'
import Action from "./components/auth/Action"
import AdminDashboard from './components/admin/AdminDashboard'
import { getDoc, doc } from "firebase/firestore"
import { db } from "./lib/firebase"

function ProtectedRoute({ children }) {
  const { user } = useAuth()
  if (!user) {
    return <Navigate to="/login" replace />
  }
  if (!user.emailVerified) {
    return <Login showVerificationPrompt={true} />
  }
  return children
}

function AuthRoute({ children }) {
  const { user } = useAuth()
  // If user exists but email isn't verified, show verification prompt
  if (user && !user.emailVerified) {
    return <Login showVerificationPrompt={true} />
  }
  // Otherwise follow normal auth flow
  return user ? <Navigate to="/chat" replace /> : children
}

function ProtectedAdminRoute({ children }) {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(userDoc.exists() && userDoc.data().admin === true);
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
      setLoading(false);
    };

    checkAdminStatus();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppContent() {
  const { loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    )
  }

  return (
    <Routes>
      <Route path="/" element={<AuthRoute><LandingPage /></AuthRoute>} />
      <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
      <Route path="/signup" element={<AuthRoute><Login isSignup /></AuthRoute>} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/action" element={<Action />} />
      <Route path="/plans" element={<ProtectedRoute><Plans /></ProtectedRoute>} />
      <Route path="/chat" element={
        new URLSearchParams(window.location.search).get('share') ? (
          <SharedDebateView />
        ) : (
          <ProtectedRoute>
            <ChatInterface />
          </ProtectedRoute>
        )
      } />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/admin" element={
        <ProtectedAdminRoute>
          <AdminDashboard />
        </ProtectedAdminRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  useEffect(() => {
    initializeRemoteConfig().catch(console.error)
  }, [])

  return (
    <Router>
      <AuthProvider>
        <SubscriptionProvider>
          <div className="min-h-screen bg-gray-900 text-white">
            <AppContent />
          </div>
        </SubscriptionProvider>
      </AuthProvider>
    </Router>
  )
}

export default App

